import type { ReadConfig } from 'api-read-hook';
import type { HydraCollection } from '../../types';

import { useApiRead } from 'api-read-hook';

import { makeQueryString } from '../../util';
import * as TicketLabel from './TicketLabel';
import * as Upload from './Upload';
import * as User from './User';
import * as WorkDone from './WorkDone';

export type BaseView = {
  '@id': string;
  '@type': 'Ticket';
  id: string;
  number: number;
  title: string;
  body: string | null;
  createdAt: string;
  closedAt: string | null;
  updatedAt: string | null;
};

export type V_AdminList = BaseView & {
  company: string;
};

export type V_AvailableList = BaseView & {
  company: {
    '@id': string;
    '@type': 'Company';
    slug: string;
    name: string;
    backgroundColor: string | null;
  };
  skills: Array<{
    '@id': string;
    '@type': 'TicketSkill';
    id: string;
    type: string;
    approved: boolean | null;
    assignee: User.BaseView | null;
    status: string | null;
  }>;
  priority: string | null;
};

export type V_AwaitingReviewList = BaseView & {
  company: {
    '@id': string;
    '@type': 'Company';
    slug: string;
    name: string;
    backgroundColor: string | null;
  };
  skills: Array<{
    '@id': string;
    '@type': 'TicketSkill';
    id: string;
    type: string;
    approved: boolean | null;
    assignee: User.BaseView | null;
    status: string | null;
  }>;
  priority: string | null;
};

export type V_Detail = BaseView & {
  createdBy: User.BaseView;
  release: {
    '@id': string;
    '@type': 'Release';
    versionNumber: string;
    platform: string | null;
  } | null;
  myHasCommented: boolean | null;
  myNotifyPref: {
    '@id': string;
    '@type': 'ItemNotifyPref';
    notify: boolean;
  } | null;
  memberUsers: Array<User.BaseView>;
  labels: Array<TicketLabel.BaseView>;
  comments: Array<{
    '@id': string;
    '@type': 'Comment';
    id: string;
    body: string;
    author: User.BaseView;
    isInternal: boolean;
    createdAt: string;
    uploads: Array<Upload.BaseView>;
  }>;
  events: Array<{
    '@id': string;
    '@type': 'TicketEvent';
    id: string;
    type: string;
    user: User.BaseView | null;
    createdAt: string;
    data: object | null;
    label: TicketLabel.BaseView | null;
    oldTitle: string | null;
    newTitle: string | null;
    mentionedByTicketNumber: number | null;
    isInternal: boolean;
  }>;
  uploads: Array<Upload.BaseView>;
  skills: Array<{
    '@id': string;
    '@type': 'TicketSkill';
    id: string;
    type: string;
    approved: boolean | null;
    assignee: User.BaseView | null;
    status: string | null;
    createdAt: string;
  }>;
  pullRequests: Array<{
    '@id': string;
    '@type': 'GitHubPullRequest';
    id: string;
    repo: {
      '@id': string;
      '@type': 'CompanyGitHubRepo';
      id: string;
      name: string;
      skill: string;
      createdAt: string;
    };
    number: number;
    title: string;
    url: string;
    authorGithubUsername: string | null;
    author: User.BaseView | null;
    isDraft: boolean;
    openedAt: string;
    githubUpdatedAt: string;
    mergedAt: string | null;
    closedAt: string | null;
    state: string;
    latestReviews: Array<{
      '@id': string;
      '@type': 'GitHubPullRequestReview';
      id: string;
      reviewerGithubUsername: string;
      reviewer: User.BaseView | null;
      state: string;
      submittedAt: string;
    }>;
  }>;
  workDones: Array<
    WorkDone.BaseView & {
      user: User.BaseView;
    }
  >;
};

export type V_List = BaseView & {
  createdBy: User.BaseView;
  release: {
    '@id': string;
    '@type': 'Release';
    versionNumber: string;
    platform: string | null;
  } | null;
  myHasCommented: boolean | null;
  myNotifyPref: {
    '@id': string;
    '@type': 'ItemNotifyPref';
    notify: boolean;
  } | null;
  memberUsers: Array<User.BaseView>;
  searchSnippet: string | null;
  labels: Array<TicketLabel.BaseView>;
  comments: Array<string>;
  workDones: Array<{
    '@id': string;
    '@type': 'WorkDone';
    user: User.BaseView;
    minutes: number | null;
    loggedAt: string | null;
  }>;
};

export type V_UntriagedList = BaseView & {
  company: {
    '@id': string;
    '@type': 'Company';
    slug: string;
    name: string;
    backgroundColor: string | null;
  };
  skills: Array<{
    '@id': string;
    '@type': 'TicketSkill';
    id: string;
    type: string;
    approved: boolean | null;
    assignee: User.BaseView | null;
    status: string | null;
  }>;
  priority: string | null;
};

export type W_Create = {
  company: string;
  title: string;
  body: string | null;
  labels: Array<string>;
  uploads: Array<string>;
};

export type W_Update_partial = Partial<{
  title: string;
  closedAt: string | null;
  labels: Array<string>;
}>;

export function useReadCollection(
  queryParams?: {
    assignee?: string;
    'assignee[]'?: Array<string>;
    company?: string;
    'company[]'?: Array<string>;
    'exists[closedAt]'?: boolean;
    labels?: string;
    'order[createdAt]'?: string;
    'order[updatedAt]'?: string;
    page?: number;
    search?: string;
  },
  options?: ReadConfig
) {
  return useApiRead<HydraCollection<V_List>>(
    `/tickets${makeQueryString(queryParams)}`,
    options
  );
}

export function useReadCollectionAdminList(
  queryParams?: { company?: string; 'company[]'?: Array<string> },
  options?: ReadConfig
) {
  return useApiRead<HydraCollection<V_AdminList>>(
    `/tickets/admin-list${makeQueryString(queryParams)}`,
    options
  );
}

export function useReadCollectionAvailable(
  queryParams?: { page?: number; user?: string },
  options?: ReadConfig
) {
  return useApiRead<HydraCollection<V_AvailableList>>(
    `/tickets/available${makeQueryString(queryParams)}`,
    options
  );
}

export function useReadCollectionAwaitingReview(
  queryParams?: { page?: number },
  options?: ReadConfig
) {
  return useApiRead<HydraCollection<V_AwaitingReviewList>>(
    `/tickets/awaiting-review${makeQueryString(queryParams)}`,
    options
  );
}

export function useReadCollectionUntriaged(
  queryParams?: { page?: number },
  options?: ReadConfig
) {
  return useApiRead<HydraCollection<V_UntriagedList>>(
    `/tickets/untriaged${makeQueryString(queryParams)}`,
    options
  );
}

export function useReadItemCompanySub(
  companyId: string,
  number: string,
  options?: ReadConfig
) {
  return useApiRead<V_Detail>(
    `/tickets/number/${companyId}/${number}`,
    options
  );
}
